/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

export const SIDE_NAV_COOKIE_PREFIX = 'sf_side_nav_'

export const SIDE_NAV_COOKIE_NAME = `${SIDE_NAV_COOKIE_PREFIX}expanded`

const SIDE_NAV_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365

const listenersByCookieName = new Map<string, Set<() => void>>()

function escapeCookieName(cookieName: string): string {
  return cookieName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function parseExpandedCookieValue(raw: string | undefined): boolean | undefined {
  if (raw === 'true') {
    return true
  }
  if (raw === 'false') {
    return false
  }
  return undefined
}

function readExpandedCookieFromHeader(cookieHeader: string, cookieName: string): boolean | undefined {
  const match = cookieHeader.match(new RegExp(`(?:^|; )${escapeCookieName(cookieName)}=([^;]*)`))
  if (match?.[1] == null) {
    return undefined
  }

  return parseExpandedCookieValue(decodeURIComponent(match[1]))
}

/** Parses SideNav collapse cookies from a Cookie header. Only `sf_side_nav_*` names are included. */
export function parseSideNavExpandedCookies(cookieHeader: string): Record<string, boolean> {
  const result: Record<string, boolean> = {}
  if (cookieHeader === '') {
    return result
  }

  for (const part of cookieHeader.split(';')) {
    const trimmed = part.trim()
    const separatorIndex = trimmed.indexOf('=')
    if (separatorIndex <= 0) {
      continue
    }

    const name = trimmed.slice(0, separatorIndex)
    if (!name.startsWith(SIDE_NAV_COOKIE_PREFIX)) {
      continue
    }

    const value = parseExpandedCookieValue(decodeURIComponent(trimmed.slice(separatorIndex + 1)))
    if (value !== undefined) {
      result[name] = value
    }
  }

  return result
}

export function readSideNavExpandedCookie(cookieName: string): boolean | undefined {
  if (typeof document === 'undefined') {
    return undefined
  }

  return readExpandedCookieFromHeader(document.cookie, cookieName)
}

export function writeSideNavExpandedCookie(cookieName: string, expanded: boolean): void {
  if (typeof document === 'undefined') {
    return
  }

  const secure = window.location.protocol === 'https:'
  // Cookie Store API is not available in all browsers we support.
  // biome-ignore lint/suspicious/noDocumentCookie: Essential UI-chrome cookie for SideNav collapse state.
  document.cookie = `${cookieName}=${expanded}; path=/; max-age=${SIDE_NAV_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${
    secure ? '; Secure' : ''
  }`
  notifySideNavCookie(cookieName)
}

export function subscribeSideNavCookie(cookieName: string, listener: () => void): () => void {
  let listeners = listenersByCookieName.get(cookieName)
  if (!listeners) {
    listeners = new Set()
    listenersByCookieName.set(cookieName, listeners)
  }

  listeners.add(listener)

  return () => {
    listeners.delete(listener)
    if (listeners.size === 0) {
      listenersByCookieName.delete(cookieName)
    }
  }
}

function notifySideNavCookie(cookieName: string): void {
  const listeners = listenersByCookieName.get(cookieName)
  if (!listeners) {
    return
  }

  for (const listener of listeners) {
    listener()
  }
}
